import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowRight, ArrowUpRight, Copy, Check, Mic, Square, Volume2, VolumeX, Maximize2, Minimize2,
} from 'lucide-react';
import { assistantService } from '../services/carehome_manager';
import { useAuth } from '../context/AuthContext';
import { assistantActionHref } from '../utils/assistantLinks';
import patchAiIcon from '../assets/patch-ai-icon.svg';

// Browser speech APIs. Recognition is Chrome / Edge / Safari only, so the
// microphone button is simply not drawn where it is missing (e.g. Firefox).
const SpeechRecognitionImpl = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition || null)
  : null;
const canSpeakAloud = typeof window !== 'undefined' && 'speechSynthesis' in window;

// The browser's recogniser stops at the first breath on its own, so Patch
// listens continuously and decides itself when the question is over: after a
// real pause, when the stop button is tapped, or at the one-minute cap.
const PAUSE_ENDS_SPEECH_MS = 4000;
const WAIT_FOR_SPEECH_MS = 8000;
const MAX_LISTEN_MS = 60000;
const MAX_RECOGNISER_RESTARTS = 5;
const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

const VOICE_NOTES = {
  blocked: 'Microphone access is blocked. Allow it for this site in your browser to speak to Patch.',
  nothing: "I didn't hear anything — tap the microphone and try again.",
  noMic: 'No microphone was found. Connect one, or type your question.',
  network: 'Voice input needs an internet connection. Please type your question instead.',
  capped: 'Patch listens for up to a minute at a time — tap the microphone to keep going.',
  other: 'Voice input stopped. Please try again or type your question.',
};
const voiceNoteFor = (error) => ({
  'not-allowed': VOICE_NOTES.blocked,
  'service-not-allowed': VOICE_NOTES.blocked,
  'audio-capture': VOICE_NOTES.noMic,
  network: VOICE_NOTES.network,
}[error] || VOICE_NOTES.other);

const joinWords = (...parts) => parts.map((p) => (p || '').trim()).filter(Boolean).join(' ');

// One recogniser run's pieces joined into a line. Android Chrome repeats
// everything said so far in each new piece, so there a piece that starts with
// the text so far replaces it instead of being added on.
const addHeard = (soFar, piece) => {
  const next = (piece || '').trim();
  if (!next) return soFar;
  if (!soFar) return next;
  if (isAndroid && next.toLowerCase().startsWith(soFar.toLowerCase())) return next;
  return `${soFar} ${next}`;
};

/** Stop a listening session's recogniser and timers. `discard` drops words still in flight. */
const stopVoiceSession = (session, discard) => {
  if (!session) return;
  session.over = true;
  session.discard = session.discard || discard;
  clearTimeout(session.pauseTimer);
  clearTimeout(session.maxTimer);
  try {
    if (discard) session.recognition.abort();
    else session.recognition.stop();
  } catch { /* already ended */ }
};

// The wave is mirrored: bar 0 sits in the middle and carries the lowest
// (loudest) voice frequencies, so it swells from the centre outwards.
const WAVE_HALF = 12;

// A second microphone capture for the level meter fights the recogniser on
// phones and tablets, so touch screens get the animated (not live) wave.
const canMeterVoice = typeof window !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && !!(window.AudioContext || window.webkitAudioContext)
  && !window.matchMedia?.('(pointer: coarse)').matches;

/**
 * Live microphone level while Patch listens, for the wave and the glow round
 * the stop button. Written straight onto `targetRef` as CSS variables each
 * frame (--patch-level, --patch-bar-0…11) so the chat doesn't re-render sixty
 * times a second. Returns whether real levels are flowing; until they are,
 * the wave runs its CSS loop instead.
 */
const useVoiceMeter = (active, targetRef) => {
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!active || !canMeterVoice) return undefined;
    let cancelled = false;
    let stream = null;
    let audio = null;
    let frame = 0;

    // Raw input: echo cancelling / auto-gain here would only fight the recogniser.
    navigator.mediaDevices
      .getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } })
      .then((captured) => {
        if (cancelled) {
          captured.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = captured;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        audio = new AudioCtx();
        audio.resume?.().catch(() => {});
        const analyser = audio.createAnalyser();
        analyser.fftSize = 256;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -25;
        analyser.smoothingTimeConstant = 0.7;
        audio.createMediaStreamSource(stream).connect(analyser);
        const bins = new Uint8Array(analyser.frequencyBinCount);

        const draw = (now) => {
          analyser.getByteFrequencyData(bins);
          const el = targetRef.current;
          if (el) {
            let total = 0;
            for (let i = 0; i < WAVE_HALF; i += 1) {
              // ~190 Hz per bin at 48 kHz: bins 1–24 cover the speaking voice.
              const raw = (bins[1 + i * 2] + bins[2 + i * 2]) / 510;
              const level = Math.min(1, Math.max(0, (raw - 0.2) / 0.65));
              total += level;
              // A faint ripple keeps the wave alive between words.
              const idle = 0.14 + 0.05 * Math.sin(now / 260 + i * 0.9);
              el.style.setProperty(`--patch-bar-${i}`, Math.max(idle, level).toFixed(3));
            }
            el.style.setProperty('--patch-level', (total / WAVE_HALF).toFixed(3));
          }
          frame = requestAnimationFrame(draw);
        };
        frame = requestAnimationFrame(draw);
        setLive(true);
      })
      .catch(() => { /* no meter — the CSS wave still plays */ });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      stream?.getTracks().forEach((track) => track.stop());
      audio?.close().catch(() => {});
      const el = targetRef.current;
      if (el) {
        el.style.removeProperty('--patch-level');
        for (let i = 0; i < WAVE_HALF; i += 1) el.style.removeProperty(`--patch-bar-${i}`);
      }
      setLive(false);
    };
  }, [active, targetRef]);

  return live;
};

/** Seconds since listening began, as 0:07. Ticks on its own so the panel doesn't. */
const ListenClock = () => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="text-[12px] font-medium tabular-nums text-slate-400">
      {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
    </span>
  );
};

/** The voice wave shown while Patch listens — live levels where metered, a soft loop otherwise. */
const VoiceWave = ({ live }) => (
  <div className="flex h-7 min-w-0 flex-1 items-center justify-center gap-[3px] overflow-hidden" aria-hidden="true">
    {Array.from({ length: WAVE_HALF * 2 }, (_, i) => {
      const fromCentre = i < WAVE_HALF ? WAVE_HALF - 1 - i : i - WAVE_HALF;
      return (
        <span
          key={i}
          className={`w-[3px] shrink-0 rounded-full bg-gradient-to-t from-blue-600 to-violet-400 ${
            live ? 'h-full transition-transform duration-75 ease-out' : 'patch-wave'
          }`}
          style={live
            ? { transform: `scaleY(var(--patch-bar-${fromCentre}, 0.14))` }
            : {
              height: `${100 - fromCentre * 5}%`,
              animationDuration: `${780 + (fromCentre % 4) * 110}ms`,
              animationDelay: `-${(i * 137) % 900}ms`,
            }}
        />
      );
    })}
  </div>
);

/** An answer as it should sound: no Markdown markers read out as symbols. */
const toSpeech = (text) => String(text)
  .replace(/\*\*/g, '')
  .replace(/^\s*[-*]\s+/gm, '')
  .replace(/^\s*(\d+)\.\s+/gm, '$1. ');

// Panel size preference — a per-browser convenience, so storage failures
// (private mode, blocked site data) just fall back to the normal size.
const SIZE_KEY = 'patch.panel.expanded';
const readExpanded = () => {
  try { return window.localStorage.getItem(SIZE_KEY) === '1'; } catch { return false; }
};
const saveExpanded = (value) => {
  try { window.localStorage.setItem(SIZE_KEY, value ? '1' : '0'); } catch { /* not worth surfacing */ }
};

/**
 * PatchBot — a clean, premium robot mark for Ask Patch. Uses currentColor so it
 * sits nicely (white) on the gradient badges and bubbles.
 */
const PatchBot = ({ size = 20, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {/* antenna */}
    <path d="M12 3.5v2.2" />
    <circle cx="12" cy="2.6" r="1.15" fill="currentColor" stroke="none" />
    {/* head */}
    <rect x="4" y="5.8" width="16" height="12.4" rx="4.2" />
    {/* side ears */}
    <path d="M2 11.4v2.6M22 11.4v2.6" />
    {/* eyes */}
    <circle cx="9.2" cy="11.6" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="14.8" cy="11.6" r="1.3" fill="currentColor" stroke="none" />
    {/* big friendly smile */}
    <path d="M8.7 14.4c1.1 1.9 5.5 1.9 6.6 0" />
  </svg>
);

/* ---------------------------------------------------------------------------
 * Lightweight Markdown renderer: **bold**, "- " bullets, "1." steps, blank lines.
 * (Avoids pulling in a markdown dependency for the few things we need.)
 * ------------------------------------------------------------------------- */
const renderInline = (str, kp) =>
  String(str)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((p, i) =>
      /^\*\*[^*]+\*\*$/.test(p) ? (
        <strong key={kp + i} className="font-semibold text-slate-900 dark:text-white">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={kp + i}>{p}</React.Fragment>
      )
    );

const RichText = ({ text }) => {
  const lines = String(text).split('\n');
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  lines.forEach((line) => {
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)\.\s+(.*)$/);
    if (bullet) {
      if (!list || list.type !== 'ul') { flush(); list = { type: 'ul', items: [] }; }
      list.items.push(bullet[1]);
    } else if (numbered) {
      if (!list || list.type !== 'ol') { flush(); list = { type: 'ol', items: [] }; }
      list.items.push(numbered[2]);
    } else {
      flush();
      blocks.push({ type: line.trim() ? 'p' : 'sp', text: line });
    }
  });
  flush();
  return (
    <div className="space-y-2">
      {blocks.map((blk, i) => {
        if (blk.type === 'ul')
          return (
            <ul key={i} className="list-disc space-y-1 pl-4 marker:text-blue-400">
              {blk.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}-`)}</li>)}
            </ul>
          );
        if (blk.type === 'ol')
          return (
            <ol key={i} className="list-decimal space-y-1 pl-4 marker:font-semibold marker:text-blue-500">
              {blk.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}-`)}</li>)}
            </ol>
          );
        if (blk.type === 'sp') return <div key={i} className="h-1" />;
        return <p key={i}>{renderInline(blk.text, `${i}-`)}</p>;
      })}
    </div>
  );
};

/** Reveals text quickly (streaming feel), then renders it as rich markdown. */
const TypingText = ({ text, onTick }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    setCount(0);
    let i = 0;
    const id = setInterval(() => {
      i += 4;
      setCount(i);
      onTick?.();
      if (i >= text.length) { setCount(text.length); clearInterval(id); }
    }, 16);
    return () => clearInterval(id);
  }, [text]);
  return <RichText text={text.slice(0, count)} />;
};

const toolBtnCls = 'flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm transition-colors hover:text-blue-600 dark:border-white/10 dark:bg-[#262b33]';

/**
 * One assistant reply: robot avatar, streamed+markdown body, the "Open …"
 * buttons for the screen(s) it came from, and Listen / Copy on hover.
 */
const AssistantBubble = ({ text, links = [], onOpen, onTick }) => {
  const [copied, setCopied] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  // Whatever this reply is reading aloud stops when the reply goes away — on
  // unmount only, so another reply starting to speak is never cut off by it.
  const speakingRef = useRef(false);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  useEffect(() => () => { if (speakingRef.current) window.speechSynthesis?.cancel(); }, []);

  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const listen = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (speaking) { synth.cancel(); setSpeaking(false); return; }
    synth.cancel(); // only one answer speaks at a time
    const utterance = new window.SpeechSynthesisUtterance(toSpeech(text));
    utterance.lang = 'en-GB';
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(utterance);
  };

  return (
    <div className="patch-rise group flex items-start gap-2.5">
      <img src={patchAiIcon} alt="Patch" className="mt-0.5 h-7 w-7 shrink-0 rounded-lg shadow-sm" />
      <div className="relative max-w-[88%] rounded-2xl rounded-bl-md border border-slate-200/70 bg-white px-4 py-3 text-[13.5px] leading-relaxed text-slate-700 shadow-sm dark:border-white/10 dark:bg-[#262b33] dark:text-slate-200">
        <TypingText text={text} onTick={onTick} />
        {links.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {links.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => onOpen(link.href)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-[12px] font-semibold text-blue-600 transition-colors hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
              >
                {link.label}
                <ArrowUpRight size={13} />
              </button>
            ))}
          </div>
        )}
        <div className={`absolute -right-2 -top-2 flex gap-1 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${speaking ? 'opacity-100' : 'opacity-0'}`}>
          {canSpeakAloud && (
            <button type="button" onClick={listen} aria-label={speaking ? 'Stop reading aloud' : 'Read answer aloud'} className={toolBtnCls}>
              {speaking ? <VolumeX size={13} className="text-blue-600" /> : <Volume2 size={13} />}
            </button>
          )}
          <button type="button" onClick={copy} aria-label="Copy answer" className={toolBtnCls}>
            {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Ask Patch — the ServPatch help assistant.
 *
 * A premium right-docked panel (~30% of the screen) with a launcher button. It
 * answers "where is X / how do I use it" questions, tailored to the current
 * user's role and organisation type (see the backend assistant guide).
 */

/**
 * Quick-start prompts shown in the welcome card.
 *
 * The real list comes from the backend (assistantService.suggestions), built
 * from this user's role, organisation type and assigned menus — so a
 * domiciliary manager is never offered a care-home question. These are only the
 * offline fallback, and even they follow the org type: never hardcode a prompt
 * here that is specific to one kind of organisation.
 */
const fallbackSuggestions = (isDomiciliary) => [
  {
    label: `Add a ${isDomiciliary ? 'client' : 'resident'}`,
    question: `How do I add a new ${isDomiciliary ? 'client' : 'resident'}?`,
  },
  {
    label: isDomiciliary ? 'Assign a visit' : 'Publish the rota',
    question: isDomiciliary
      ? 'How do I assign an unassigned visit to a carer on the Rota?'
      : 'How do I edit and publish the staff rota?',
  },
  { label: 'Explain the dashboard', question: 'Give me a quick overview of the dashboard.' },
];

/**
 * The assistant panel. `open` is owned by DashboardLayout because the button
 * that opens it lives in the top header, and the panel cannot: the navbar's
 * backdrop-blur makes it a containing block for `fixed` children, which would
 * trap this panel inside a 56px-tall bar.
 */
const AssistantWidget = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { user, allAvailableMenus, menuIds } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [messages, setMessages] = useState([]); // { role, text, links? }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(readExpanded);
  const [listening, setListening] = useState(false);
  const [voiceNote, setVoiceNote] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const composerRef = useRef(null);
  const voiceRef = useRef(null); // the listening session in progress, if any
  const meterLive = useVoiceMeter(listening, composerRef);

  const isDomiciliary =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';

  const toggleExpanded = () => setExpanded((prev) => { saveExpanded(!prev); return !prev; });

  /**
   * "Open Rota" etc. The panel closes so the screen is visible; the chat is
   * kept (this layout stays mounted across routes), so reopening Patch picks
   * the conversation up where it was.
   */
  const openLink = (href) => {
    navigate(href);
    setOpen(false);
  };

  // Closing the panel stops the microphone and anything being read aloud.
  /**
   * End the current listening session. A normal stop lets the last words
   * still land in the box; `discard` (send, close) throws them away.
   */
  const endListening = (note = '', { discard = false } = {}) => {
    const session = voiceRef.current;
    if (!session) return;
    voiceRef.current = null;
    stopVoiceSession(session, discard);
    setListening(false);
    if (note) setVoiceNote(note);
  };

  useEffect(() => {
    if (open) return;
    endListening('', { discard: true });
    if (canSpeakAloud) window.speechSynthesis.cancel();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => stopVoiceSession(voiceRef.current, true), []);

  /**
   * Speak a question instead of typing it. What is heard is written into the
   * box as it comes in, and stays there to be checked and sent — a misheard
   * name is easier to fix than an answer to the wrong question.
   *
   * Listening carries on through ordinary pauses; it ends after
   * PAUSE_ENDS_SPEECH_MS of quiet, when the stop button is tapped, or at
   * MAX_LISTEN_MS. Chrome also ends its recogniser by itself now and then
   * (about a minute in, network blips) — that run's words are kept and a
   * fresh run picks up where it left off.
   */
  const startListening = () => {
    if (!SpeechRecognitionImpl || voiceRef.current) return;

    const recognition = new SpeechRecognitionImpl();
    recognition.lang = 'en-GB';
    recognition.interimResults = true;
    recognition.continuous = true;

    const session = {
      recognition,
      typedBefore: input.trim(),
      kept: '', // words from earlier recogniser runs
      run: '', // words from the run in progress
      heard: false,
      restarts: 0,
      over: false,
      discard: false,
      pauseTimer: null,
      maxTimer: null,
    };

    // Re-armed on every word: a pause of PAUSE_ENDS_SPEECH_MS ends the question.
    const waitForPause = () => {
      clearTimeout(session.pauseTimer);
      session.pauseTimer = setTimeout(
        () => endListening(session.heard ? '' : VOICE_NOTES.nothing),
        session.heard ? PAUSE_ENDS_SPEECH_MS : WAIT_FOR_SPEECH_MS
      );
    };

    recognition.onresult = (event) => {
      if (session.discard) return;
      let run = '';
      for (let i = 0; i < event.results.length; i += 1) run = addHeard(run, event.results[i][0].transcript);
      session.run = run;
      if (run) session.heard = true;
      setInput(joinWords(session.typedBefore, session.kept, session.run));
      if (!session.over) waitForPause();
    };
    recognition.onspeechstart = () => {
      if (!session.over) waitForPause();
    };
    recognition.onerror = (event) => {
      if (session.over) return;
      // 'no-speech' just ends this run; the restart below and the pause timer handle it.
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      endListening(voiceNoteFor(event.error));
    };
    recognition.onend = () => {
      if (session.over) {
        if (!session.discard) inputRef.current?.focus();
        return;
      }
      session.kept = joinWords(session.kept, session.run);
      session.run = '';
      session.restarts += 1;
      if (session.restarts > MAX_RECOGNISER_RESTARTS) {
        endListening(session.heard ? '' : VOICE_NOTES.other);
        return;
      }
      try {
        recognition.start();
      } catch {
        endListening(VOICE_NOTES.other);
      }
    };

    try {
      recognition.start();
    } catch {
      setVoiceNote(VOICE_NOTES.other);
      return;
    }
    voiceRef.current = session;
    setVoiceNote('');
    setListening(true);
    waitForPause();
    session.maxTimer = setTimeout(() => endListening(VOICE_NOTES.capped), MAX_LISTEN_MS);
  };

  const toggleListening = () => {
    if (voiceRef.current) endListening();
    else startListening();
  };

  // The user's real situation, so answers use the exact menus, terminology and
  // Rota this user actually sees (care home vs domiciliary, assigned menus only).
  const profile = useMemo(() => {
    const menuCodes = (allAvailableMenus || [])
      .filter((m) => (menuIds || []).includes(m.id))
      .map((m) => m.code);
    return {
      roleCode: user?.role?.role_code || null,
      orgType: user?.organization?.organization_type || null,
      menuCodes,
    };
  }, [user, allAvailableMenus, menuIds]);

  const scrollToEnd = (behavior = 'smooth') => endRef.current?.scrollIntoView({ behavior, block: 'end' });

  useEffect(() => {
    scrollToEnd();
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  // Load the tailored quick-start prompts once the user's profile is known.
  // Falls back to the org-type-aware defaults if the request fails.
  useEffect(() => {
    let cancelled = false;
    if (!profile.roleCode) return undefined;
    assistantService.suggestions(profile).then((list) => {
      if (cancelled) return;
      setSuggestions(list.length ? list : fallbackSuggestions(isDomiciliary));
    });
    return () => {
      cancelled = true;
    };
  }, [profile, isDomiciliary]);

  const sendMessage = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || loading) return;
    // Sent as shown; words still in flight must not refill the cleared box.
    endListening('', { discard: true });
    setVoiceNote('');

    const nextMessages = [...messages, { role: 'user', text }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const history = nextMessages.map((m) => ({ role: m.role, text: m.text }));
      const data = await assistantService.ask(text, history, profile);
      // The API says WHICH screens (only ones this user may open); the route
      // for each is worked out here. Anything without a route is dropped.
      const links = (Array.isArray(data?.actions) ? data.actions : [])
        .map((action) => ({ label: action.label, href: assistantActionHref(action, { isDomiciliary }) }))
        .filter((link) => link.label && link.href);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: data?.reply || "Sorry, I couldn't find an answer.",
          links,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Something went wrong. Please try again after some time.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Scoped keyframes (no animate plugin in this project) */}
      <style>{`
        @keyframes patchRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes patchPop  { from { opacity: 0; transform: scale(.9); } to { opacity: 1; transform: scale(1); } }
        .patch-rise   { animation: patchRise .35s cubic-bezier(.22,1,.36,1) both; }
        .patch-pop    { animation: patchPop .4s cubic-bezier(.22,1,.36,1) both; }
        @keyframes patchWave { 0%, 100% { transform: scaleY(.2); } 50% { transform: scaleY(1); } }
        .patch-wave   { animation: patchWave .9s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .patch-wave { animation: none; transform: scaleY(.45); } }
      `}</style>

      {/* Invisible click-catcher to close on outside click (no dimming). */}
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-40 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />

      {/* Floating rounded panel that POPS open from the corner. Two sizes: the
          everyday one (~40% of the screen) and an expanded one for reading
          long answers, toggled from the header and remembered per browser. */}
      <div
        role="dialog"
        aria-label="Ask Patch assistant"
        style={{ transformOrigin: 'top right' }}
        className={`fixed bottom-4 right-4 top-4 z-50 flex w-[calc(100%-2rem)] flex-col overflow-hidden rounded-[26px] bg-[#F7F9FC] shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)] ring-1 ring-black/5 transition-all duration-300 ease-[cubic-bezier(.22,1,.36,1)] dark:bg-[#1b1f26] dark:ring-white/10 ${
          expanded ? 'md:w-[min(1040px,calc(100%-2rem))]' : 'md:w-[40%] md:min-w-[440px] md:max-w-[600px]'
        } ${
          open ? 'scale-100 opacity-100' : 'pointer-events-none scale-90 opacity-0'
        }`}
      >
        {/* Header — subtle brand-gradient wash for a premium feel */}
        <div className="flex items-center gap-3 border-b border-slate-200/70 bg-gradient-to-r from-indigo-50 via-white to-white px-4 py-3.5 dark:border-white/10 dark:from-[#20242c] dark:via-[#20242c] dark:to-[#20242c]">
          <img src={patchAiIcon} alt="Patch" className="h-10 w-10 rounded-xl shadow-[0_6px_16px_-4px_rgba(37,99,235,0.6)]" />
          <div className="flex-1 leading-tight">
            <p className="text-[15px] font-bold text-slate-800 dark:text-slate-100">Patch</p>
            <p className="text-[11px] font-medium text-slate-400">Your ServPatch guide</p>
          </div>
          {/* Wider view — only meaningful where the panel is not already full width. */}
          <button
            type="button"
            onClick={toggleExpanded}
            aria-label={expanded ? 'Make the panel smaller' : 'Make the panel bigger'}
            title={expanded ? 'Smaller' : 'Bigger'}
            className="hidden h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 md:flex"
          >
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="ml-1 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10"
          >
            <X size={18} />
          </button>
        </div>

        {/* Conversation — kept to a readable column when the panel is wide. */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="mx-auto w-full max-w-3xl space-y-4">
          {/* Welcome card */}
          <div className="patch-rise rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-[#262b33]">
            <p className="text-[16px] font-bold text-slate-800 dark:text-slate-100">Hi, I'm Patch. How can I help?</p>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
              Ask how to do something in ServPatch — or ask about your own records,
              like who's on leave today or which residents had an incident this month.
            </p>
            <div className="mt-3.5 flex flex-col gap-2">
              {(suggestions.length ? suggestions : fallbackSuggestions(isDomiciliary)).map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => sendMessage(s.question)}
                  disabled={loading}
                  style={{ animationDelay: `${120 + i * 70}ms` }}
                  className={`patch-rise group/chip flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left text-[13px] font-semibold text-slate-700 transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50/50 hover:text-blue-700 hover:shadow-md disabled:opacity-50 dark:border-white/10 dark:bg-[#262b33] dark:text-slate-200 dark:hover:border-blue-500/40 dark:hover:bg-blue-500/10 ${
                    i === 0 ? 'shadow-sm' : ''
                  }`}
                >
                  {s.label}
                  <ArrowUpRight size={15} className="text-slate-300 transition-transform group-hover/chip:translate-x-0.5 group-hover/chip:-translate-y-0.5" />
                </button>
              ))}
            </div>
          </div>

          {/* Messages */}
          {messages.map((m, i) =>
            m.role === 'user' ? (
              <div key={i} className="patch-rise flex justify-end">
                <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-blue-600 px-4 py-2.5 text-[13.5px] font-medium text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]">
                  {m.text}
                </div>
              </div>
            ) : (
              <AssistantBubble key={i} text={m.text} links={m.links} onOpen={openLink} onTick={() => scrollToEnd('auto')} />
            )
          )}

          {/* Typing indicator */}
          {loading && (
            <div className="patch-rise flex items-start gap-2.5">
              <img src={patchAiIcon} alt="Patch" className="mt-0.5 h-7 w-7 shrink-0 rounded-lg shadow-sm" />
              <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-slate-200/70 bg-white px-4 py-3.5 shadow-sm dark:border-white/10 dark:bg-[#262b33]">
                <span className="h-2 w-2 animate-bounce rounded-full bg-blue-400 [animation-delay:-0.3s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-blue-400 [animation-delay:-0.15s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-blue-400" />
              </div>
            </div>
          )}
          <div ref={endRef} />
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-slate-200/70 bg-white px-5 py-3.5 dark:border-white/10 dark:bg-[#20242c]">
          <div className="mx-auto w-full max-w-3xl">
          {voiceNote && (
            <p role="status" className="mb-2 text-[12px] font-medium text-amber-600 dark:text-amber-400">{voiceNote}</p>
          )}
          {/* While listening the box glows with the voice (--patch-level is
              written here by useVoiceMeter) and a wave strip sits on top. */}
          <div
            ref={composerRef}
            style={listening ? { boxShadow: '0 0 0 calc(3px + var(--patch-level, 0) * 7px) rgba(99, 102, 241, 0.14)' } : undefined}
            className={`rounded-2xl border bg-[#F7F9FC] p-1.5 pl-4 transition-[border-color,background-color,box-shadow] duration-150 focus-within:border-blue-400 focus-within:bg-white dark:bg-[#262b33] dark:focus-within:bg-[#2c313a] ${
              listening ? 'border-indigo-300 dark:border-indigo-400/50' : 'border-slate-200 dark:border-white/10'
            }`}
          >
          {listening && (
            <div className="patch-rise flex items-center gap-3 border-b border-slate-200/70 pb-2 pr-2 pt-1.5 dark:border-white/10">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inset-0 animate-ping rounded-full bg-rose-400 opacity-75" />
                <span className="relative h-2.5 w-2.5 rounded-full bg-rose-500" />
              </span>
              <span className="text-[12px] font-semibold text-slate-600 dark:text-slate-300">Listening</span>
              <ListenClock />
              <VoiceWave live={meterLive} />
              <span className="hidden shrink-0 items-center gap-1 text-[11px] font-medium text-slate-400 sm:flex">
                Pause or tap <Square size={9} className="fill-current" /> to finish
              </span>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={listening ? 'Listening… speak your question' : 'Ask Patch anything — how-to or your data…'}
              className="max-h-40 min-h-[44px] flex-1 resize-none bg-transparent py-2 text-[14px] leading-relaxed text-slate-800 placeholder:text-slate-400 focus:outline-none dark:text-slate-100"
            />
            {SpeechRecognitionImpl && (
              <button
                type="button"
                onClick={toggleListening}
                disabled={loading}
                aria-label={listening ? 'Stop listening' : 'Speak your question'}
                aria-pressed={listening}
                title={listening ? 'Stop listening' : 'Speak your question'}
                className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
                  listening
                    ? 'bg-rose-500 text-white shadow-[0_6px_16px_-6px_rgba(244,63,94,0.7)]'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-blue-600 dark:text-slate-400 dark:hover:bg-white/10'
                }`}
              >
                {listening && (
                  <span
                    aria-hidden="true"
                    className={`absolute inset-0 rounded-xl bg-rose-400/40 ${meterLive ? 'transition-transform duration-100 ease-out' : 'animate-ping'}`}
                    style={meterLive ? { transform: 'scale(calc(1 + var(--patch-level, 0) * 0.7))' } : undefined}
                  />
                )}
                {listening ? <Square size={14} className="relative fill-current" /> : <Mic size={18} />}
              </button>
            )}
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              aria-label="Send"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.7)] transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowRight size={18} />
            </button>
          </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssistantWidget;
